import { logException } from "@wss/error-tracking";
import { useEffect, useState } from "react";
import fetchPlusModalData from "~/common/PlusSubscriptionModal/api/fetchPlusModalData";
import {
  EnrollmentOption,
  PlusModalData,
} from "~/common/PlusSubscriptionModal/Models";

/**
 * Custom React hook for fetching and managing Plus Subscription modal data.
 *
 * This hook retrieves Plus modal data from the API, extracts available promotions,
 * and determines the default shipping address ID for the user. It also logs any errors
 * encountered during the fetch operation.
 *
 * @returns {{
 *   plusPromotions: EnrollmentOption[] | undefined,
 *   plusModalData: PlusModalData | undefined,
 *   defaultShippingAddressId: number
 * }} An object containing the available Plus promotions, the raw Plus modal data,
 *     and the default shipping address ID.
 *
 * @example
 * const { plusPromotions, plusModalData, defaultShippingAddressId } = usePlusModalData();
 */
export default function usePlusModalData(): {
  plusPromotions: readonly EnrollmentOption[] | undefined;
  plusModalData: PlusModalData | undefined;
  defaultShippingAddressId: number;
} {
  const [plusPromotions, setPlusPromotions] = useState<EnrollmentOption[]>();
  const [plusModalData, setPlusModalData] = useState<PlusModalData>();
  const [defaultShippingAddressId, setDefaultShippingAddressId] = useState(0);

  useEffect(() => {
    const setPlusSignupModalData = async () => {
      const result = await fetchPlusModalData();

      if (result.isError) {
        logException(result.error);
        return;
      }

      const plusModalData = result.value;
      setPlusModalData(plusModalData);
      setPlusPromotions(
        plusModalData?.enrollmentOptions?.filter(
          (option) => option.promotionName,
        ),
      );

      const addresses = plusModalData?.validAddresses ?? [];
      const defaultId =
        addresses.find((address) => address.isDefault)?.id ??
        addresses[0]?.id ??
        0;

      setDefaultShippingAddressId(defaultId);
    };

    setPlusSignupModalData();
  }, []);

  return {
    plusPromotions,
    plusModalData,
    defaultShippingAddressId,
  };
}
